import { Injectable } from '@angular/core';
import {
  AttributeFilterHandler,
  CategoriesFilterHandler,
  PriceFilterHandler,
  PromotionFilterHandler,
  RangeFilterHandler,
} from '~app/modules/filters/classes';
import { Params } from '@angular/router';
import { PriceFilterRange, RangeFilters } from '~shared/models';
import { GeneralUtil } from '~shared/utils/general.util';
import { FilterRouteService } from '~shared/services/filter-route.service';
import { FiltersApplicationEventService } from '~shared/services/filters-application-event.service';
import { CategoryFilter, PromotionFilterChangedPayload } from '~app/modules/filters/models';
import { FilterHandlerParams } from '~app/modules/filters/models/filter-handler-params';
import { BehaviorSubject, Subject } from 'rxjs';
import { blackDealFilterChangedPayload } from '~app/modules/filters/models/black-deal-filter-changed-payload.model';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private mobilePreviousSavedFilter: Params;
  private params: Params;

  private categoryReset = new BehaviorSubject<boolean>(true);
  categoryReset$ = this.categoryReset.asObservable();

  constructor(
    private filtersApplicationEventService: FiltersApplicationEventService,
    private filterRouteService: FilterRouteService
  ) {}

  /* Params methods */
  updateQueryParams(params: Params): void {
    PriceFilterHandler.instance.updateQueryParams(params);
    CategoriesFilterHandler.instance.updateQueryParams(params);
    PromotionFilterHandler.instance.updateQueryParams(params);
    AttributeFilterHandler.instance.updateQueryParams(params);
    RangeFilterHandler.instance.updateQueryParams(params);
    this.filtersApplicationEventService.updateQueryParams(params);
    this.params = params;
  }

  saveMobileState(currentAppliedFilterParams: Params): void {
    this.mobilePreviousSavedFilter = currentAppliedFilterParams;
  }

  /* Apply Methods */
  filterByPrice(priceRanges: PriceFilterRange): void {
    this.filtersApplicationEventService.priceFilterChanged(priceRanges);
    this.filterRouteService.applyFilters(PriceFilterHandler.instance.apply(priceRanges));
  }

  filterByRange(ranges: RangeFilters): void {
    this.filtersApplicationEventService.rangeFilterChanged(ranges);
    this.filterRouteService.applyFilters(RangeFilterHandler.instance.apply(ranges));
  }

  filterByCategories(categoriesSlugs: CategoryFilter[]): void {
    this.filtersApplicationEventService.categoryFilterChanged(categoriesSlugs);
    this.filterRouteService.applyFilters(CategoriesFilterHandler.instance.apply(categoriesSlugs));
  }

  filterByBlackDeals(blackDeal: blackDealFilterChangedPayload): void {
    const filters: FilterHandlerParams = PromotionFilterHandler.instance.apply(blackDeal);
    this.filterRouteService.applyFilters(filters);
  }

  filterByPromotion(promotionFilterChangedPayload: PromotionFilterChangedPayload): void {
    this.filtersApplicationEventService.promotionFilterChanged(promotionFilterChangedPayload);
    const filters: FilterHandlerParams = PromotionFilterHandler.instance.apply(promotionFilterChangedPayload);
    this.filterRouteService.applyFilters(filters);
  }

  filterByVolumePrice(promotionFilterChangedPayload: PromotionFilterChangedPayload): void {
    this.filtersApplicationEventService.volumePricesFilterChanged(promotionFilterChangedPayload);
    const filters: FilterHandlerParams = PromotionFilterHandler.instance.apply(promotionFilterChangedPayload);
    this.filterRouteService.applyFilters(filters);
  }

  filterByAttribute(attrs: { [key: string]: {}; content: string }, attributeId?: string): void {
    this.filtersApplicationEventService.attributeFilterChanged(attrs.selectedAttrs, attrs.content);
    this.filterRouteService.applyFilters(AttributeFilterHandler.instance.apply(attrs.selectedAttrs, attributeId));
  }

  /* Reset Methods */
  resetCategoryFilter(): void {
    this.filtersApplicationEventService.CategoryReset();
    this.filterRouteService.applyFilters(CategoriesFilterHandler.instance.reset());
    this.categoryReset.next(true);
  }

  resetAttributeFilter(attrId: string): void {
    this.filtersApplicationEventService.attributeReset(attrId);
    this.filterRouteService.applyFilters(AttributeFilterHandler.instance.reset(attrId));
  }

  resetRangeFilter(type: string): void {
    this.filterRouteService.applyFilters(RangeFilterHandler.instance.remove(type));
  }

  resetFilterToPreviousState(params: Params): void {
    if (!this.mobilePreviousSavedFilter) {
      return;
    }
    const lastFilter = this.mobilePreviousSavedFilter;
    const currentFilter = params.filter;

    if (currentFilter) {
      const keys = Object.keys(currentFilter);
      for (const key of keys) {
        if (currentFilter[key] && !lastFilter[key]) {
          lastFilter[key] = GeneralUtil.copyObjectWithoutValues(currentFilter[key]);
        }
      }
    }

    this.filterRouteService.applyFilters({ params: { filter: lastFilter } });
    this.mobilePreviousSavedFilter = null;
  }
}
